import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService } from './index3.js';
import { T as TranslationService } from './index6.js';
import { r as remCalc } from './index5.js';
import { d as defineCustomElement$2 } from './roche-icon2.js';

const rocheStockPriceCss = ":host{display:block;margin-top:3rem;margin-top:0;display:flex}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}.stock-price-link{text-decoration:none}.heading{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--caption--font-size--small-up);line-height:1.1875rem;color:#706b69}@media (min-width: 48rem){.heading{font-size:var(--caption--font-size--medium-up);line-height:1.375rem}}.stock-price{margin:0.5rem 0 0.25rem}.stock-price__value{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:1.75rem;line-height:2.125rem;font-weight:400;color:#000}.stock-price__value strong{font-weight:600}@media (min-width: 48rem){.stock-price__value{font-size:1.875rem;line-height:2.4375rem}}@media (min-width: 90rem){.stock-price__value{font-size:2.5rem;line-height:3.125rem}}.stock-price__currency{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--caption--font-size--small-up);line-height:1.1875rem;color:#544f4f;margin:0 0 0 0.5rem}@media (min-width: 48rem){.stock-price__currency{font-size:var(--caption--font-size--medium-up);line-height:1.375rem}}.stock-price__unavailable{padding:0;color:var(--color-text--primary);font-family:\"roche-sans\";font-style:normal;font-weight:400;font-size:1.125rem;line-height:1.6875rem;color:var(--color-text--primary)}.stock-price__unavailable strong{font-weight:600}@media (min-width: 48rem){.stock-price__unavailable{font-size:1.125rem;line-height:1.8125rem}}@media (min-width: 90rem){.stock-price__unavailable{font-size:1.25rem;line-height:1.875rem}}.stock-price-change{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;color:#544f4f;display:flex;flex-wrap:wrap}@media (min-width: 64rem){.stock-price-change{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}.stock-price-change__price-difference{margin:0 0.5rem 0 0}.stock-price-change roche-icon{margin:0 1rem 0 0}.stock-price-change .stock-down{fill:#ff1f26}.stock-price-change .stock-equal{fill:#706b69}.stock-price-change .stock-up{fill:#00b458}:host([dir=rtl]) .stock-price{display:flex;align-items:baseline}:host([dir=rtl]) .stock-price__currency{margin:0 0.5rem 0 0}:host([dir=rtl]) .stock-price-change__price-difference{margin:0 0 0 0.5rem;display:flex;flex-direction:row-reverse}:host([dir=rtl]) .stock-price-change roche-icon{margin:0 0 0 1rem}";
const RocheStockPriceStyle0 = rocheStockPriceCss;

// The number of digits after the decimal point to be displayed
const NUMBER_OF_DECIMALS = 2;
const RocheStockPrice$1 = /*@__PURE__*/ proxyCustomElement(class RocheStockPrice extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.price = undefined;
        this.currency = 'CHF';
        this.change = undefined;
        this.relativeChange = undefined;
        this.href = undefined;
        this.isUnavailable = false;
        this.translations = undefined;
    }
    /* Pretty-print a number with its sign.
      Optionally add a spacer between sign and value.
    */
    static prettyPrintNumber(value, spacer = '', numberOfDecimals = NUMBER_OF_DECIMALS) {
        const sign = ['-', '', '+'][Math.sign(value) + 1];
        const formattedNumber = Math.abs(value).toFixed(numberOfDecimals);
        return `${sign}${spacer}${formattedNumber}`;
    }
    static prettyPrintChange(difference, relativeDifference) {
        const formattedAbsoluteDifference = RocheStockPrice.prettyPrintNumber(difference, ' ');
        const formattedRelativeDifference = RocheStockPrice.prettyPrintNumber(relativeDifference);
        return (`${formattedAbsoluteDifference} (${formattedRelativeDifference}%)`);
    }
    async componentWillLoad() {
        this.translations = {
            sharePrice: await TranslationService.translate('share price'),
            today: await TranslationService.translate('today'),
            sharePriceUnavailable: await TranslationService.translate('share price currently unavailable'),
        };
    }
    componentDidLoad() {
        DomService.handleTextDirection(this.element);
    }
    render() {
        const stockChange = ['stock-down', 'stock-equal', 'stock-up'][Math.sign(this.change) + 1];
        return (h(Host, { key: '2e29718ad40f7a68d288399e36e4f573a4c66065' }, h("a", { key: '5715abefab3e1f3adbc197c16993e8db2265b7b6', class: 'stock-price-link', href: this.href }, h("div", { key: '0e2a39c0e7b7eaf579cfa61a48a9ae6dd6b7cb75', class: 'heading' }, StringService.capitalize(this.translations.sharePrice)), h("div", { key: 'c23339efd5bb2b76d2618d1860660714809c601c', class: 'stock-price' }, !this.isUnavailable && h("span", { class: 'stock-price__value' }, this.price.toFixed(NUMBER_OF_DECIMALS)), !this.isUnavailable && h("span", { class: 'stock-price__currency' }, this.currency), this.isUnavailable && h("span", { class: 'stock-price__unavailable' }, this.translations.sharePriceUnavailable)), !this.isUnavailable && h("div", { class: 'stock-price-change' }, h("roche-icon", { class: stockChange, icon: stockChange, height: `${remCalc(32)}rem` }), h("span", { class: "stock-price-change__price-difference", dir: "ltr" }, RocheStockPrice.prettyPrintChange(this.change, this.relativeChange)), h("span", { class: "stock-price-change__time-interval" }, StringService.capitalize(this.translations.today))))));
    }
    get element() { return this; }
    static get style() { return RocheStockPriceStyle0; }
}, [1, "roche-stock-price", {
        "price": [2],
        "currency": [1],
        "change": [2],
        "relativeChange": [2, "relative-change"],
        "href": [1],
        "isUnavailable": [4, "is-unavailable"],
        "translations": [32]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-stock-price", "roche-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-stock-price":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheStockPrice$1);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheStockPrice = RocheStockPrice$1;
const defineCustomElement = defineCustomElement$1;

export { RocheStockPrice, defineCustomElement };

//# sourceMappingURL=roche-stock-price.js.map